// ── Serial, throttled refresh coordination (rep portal + admin Rep Inventory tab) ──
//
// One refresher per screen. It guarantees:
//   - NEVER two overlapping fetches: a request that arrives while a fetch is running
//     does not start a second one;
//   - a NON-throttled request (a mutation, a realtime event, Retry) that arrives while a
//     fetch is running sets "run once more afterwards", because the running fetch may
//     have started before the change; the returned promise resolves only when the whole
//     loop (the running fetch AND the re-run) has finished, so `await request()` after a
//     mutation really waits for fresh data;
//   - a THROTTLED request (window focus, tab becoming visible, the 60 s timer) is
//     skipped when a fetch is already running or the last SUCCESSFUL read was less than
//     `minGapMs` (15 s) ago, so alt-tabbing cannot hammer the paged ledger read;
//   - `run()` decides success: it returns true (fresh data adopted), false (failed; the
//     caller already reported it) or "stale" (the result belongs to a previous identity
//     and was dropped: re-run at once with the current one);
//   - every run has a DEADLINE (default 45 s): the Supabase client has no request
//     timeout, and a fetch can never settle on a resumed mobile tab. A run that is still
//     pending at the deadline counts as FAILED (result false, console.warn with
//     context, `onTimeout` so the screen can show its amber banner), `busy` is cleared
//     and the next trigger (focus, interval, realtime, Retry, a mutation) starts a FRESH
//     run. A LATE result of the abandoned run is ignored: run() receives
//     `{ isCurrent }`, which turns false the moment the run is abandoned, so it must not
//     adopt its data (and the coordinator itself drops the late return value).
//
// Pure JS (no React); the clock and the timers are injectable so it can be tested.

export const MIN_REFRESH_GAP_MS = 15000;
export const REFRESH_RUN_TIMEOUT_MS = 45000;

export function createRefreshCoordinator(run, opts) {
  const o = opts || {};
  const now = typeof o.now === "function" ? o.now : () => Date.now();
  const minGapMs = o.minGapMs == null ? MIN_REFRESH_GAP_MS : o.minGapMs;
  const timeoutMs = o.timeoutMs == null ? REFRESH_RUN_TIMEOUT_MS : o.timeoutMs;
  const setT = typeof o.setTimeout === "function" ? o.setTimeout : (fn, ms) => setTimeout(fn, ms);
  const clearT = typeof o.clearTimeout === "function" ? o.clearTimeout : (id) => clearTimeout(id);
  const label = o.label || "refresh";
  let busy = false;
  let rerun = false;
  let inflight = null;
  let lastOkAt = null;
  let runToken = 0;

  /** One run under the deadline. Resolves true | false | "stale", never rejects. */
  function runOnce() {
    const token = ++runToken;
    const isCurrent = () => token === runToken;
    return new Promise((resolve) => {
      let settled = false;
      let timer = null;
      const finish = (v) => {
        if (settled) return;
        settled = true;
        if (timer != null) clearT(timer);
        resolve(v);
      };
      if (timeoutMs > 0) {
        timer = setT(() => {
          if (settled) return;
          runToken++;                                   // abandon: isCurrent() is now false for this run
          console.warn(`[${label}] refresh did not finish within ${timeoutMs} ms: counted as FAILED, a later trigger starts a fresh read`);
          if (typeof o.onTimeout === "function") {
            try { o.onTimeout(timeoutMs); } catch (e) { console.warn(`[${label}] onTimeout handler threw:`, (e && e.message) || e); }
          }
          finish(false);
        }, timeoutMs);
      }
      const failed = (e) => { if (!settled) console.warn(`[${label}] refresh threw:`, (e && e.message) || e); finish(false); };
      // run() starts synchronously (as before), a synchronous throw counts as a failed run
      let started;
      try { started = Promise.resolve(run({ isCurrent })); } catch (e) { failed(e); return; }
      started.then((r) => finish(r), failed);
    });
  }

  function request(reqOpts) {
    const throttle = !!(reqOpts && reqOpts.throttle === true);
    if (busy) {
      if (!throttle) rerun = true;
      return inflight;
    }
    if (throttle && lastOkAt != null && now() - lastOkAt < minGapMs) return Promise.resolve();
    busy = true;
    inflight = (async () => {
      try {
        do {
          rerun = false;
          const result = await runOnce();
          if (result === true) lastOkAt = now();
          else if (result === "stale") rerun = true;
        } while (rerun);
      } finally {
        busy = false;
        inflight = null;
      }
    })();
    return inflight;
  }

  return { request, isBusy: () => busy, lastOkAt: () => lastOkAt };
}

/** Wire the freshness triggers: window focus and the tab becoming visible are
 *  COALESCED into one trigger() call, and a timer fires trigger() every `intervalMs`
 *  while the tab is visible. Returns a cleanup function that removes every listener and
 *  the timer. `trigger` should call request({ throttle: true }).
 *  @param {()=>void} trigger
 *  @param {{intervalMs?:number, win?:object, doc?:object}} [opts] injectable for tests */
export function attachFreshness(trigger, opts) {
  const o = opts || {};
  const win = o.win || (typeof window !== "undefined" ? window : null);
  const doc = o.doc || (typeof document !== "undefined" ? document : null);
  const intervalMs = o.intervalMs == null ? 60000 : o.intervalMs;
  if (!win || !doc) return () => {};
  let pending = null;
  const visible = () => doc.visibilityState === "visible";
  const fire = () => {
    if (pending != null) return;                       // focus + visibilitychange arrive together: one call
    pending = setTimeout(() => {
      pending = null;
      if (visible()) trigger();
    }, 0);
  };
  const onFocus = () => fire();
  const onVisible = () => { if (visible()) fire(); };
  win.addEventListener("focus", onFocus);
  doc.addEventListener("visibilitychange", onVisible);
  const timer = setInterval(() => { if (visible()) trigger(); }, intervalMs);
  return () => {
    win.removeEventListener("focus", onFocus);
    doc.removeEventListener("visibilitychange", onVisible);
    clearInterval(timer);
    if (pending != null) { clearTimeout(pending); pending = null; }
  };
}
